import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import CurrentAssistantInfo from './CurrentAssistantInfo';
import QualificationDetails from './QualificationDetails';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'uploaded',
})(({ theme, uploaded }) => ({
  border: `2px dashed ${uploaded ? theme.palette.success.main : theme.palette.grey[300]}`,
  backgroundColor: uploaded ? theme.palette.success[50] : theme.palette.grey[50],
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: uploaded ? theme.palette.success.main : theme.palette.primary.main,
    backgroundColor: uploaded ? theme.palette.success[50] : theme.palette.primary[50],
  },
}));

// This component is now Step 2 (Qualification Overview) - Upload functionality has been moved to CurriculumAnalysisStep
const Step1Interface = ({
  qualificationId,
  onAnalyze,
  loading,
  uploadedFileInfo,
  architectConfigured,
  aiSummary,
  mappedAiSummary,
  editedValues,
  onFieldChange,
  editingField,
  onEditField,
  onCancelEdit,
  onSaveField,
  onSaveAll,
  saveLoading,
  aiPrompt,
  onAiPromptChange,
  onSendAIPrompt,
  aiPromptLoading,
  onExportPDF,
  onExportWord,
  onExportExcel,
  onProceedToNextStep,
  currentStep,
  steps
}) => {

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          Qualification Overview
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and refine the AI-generated qualification summary
        </Typography>
      </Box>

      {/* AI Assistant Info */}
      <Box sx={{ mb: 4 }}>
        <CurrentAssistantInfo
          qualificationId={qualificationId}
          step="step_2"
          onEditAssistant={() => {}}
        />
      </Box>

      {/* Analysis Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h6">
            AI Analysis
          </Typography>
          <Button
            variant="contained"
            onClick={onAnalyze}
            disabled={loading || !uploadedFileInfo}
            startIcon={loading ? <CircularProgress size={20} /> : <InfoIcon />}
          >
            {loading ? 'Awesomeness starting…' : 'Generate AI Summary'}
          </Button>
        </Box>
        
        {!uploadedFileInfo && (
          <Alert severity="info" icon={<InfoIcon />}>
            Return to Curriculum Analysis to upload a document first
          </Alert>
        )}
        
        {uploadedFileInfo && !architectConfigured && (
          <Alert severity="warning" icon={<ErrorIcon />}>
            Configure system architect before analysis
          </Alert>
        )}
      </Paper>

      {/* Full-screen loader overlay during analyze */}
      {loading && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <CircularProgress size={72} sx={{ color: '#fff', mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800 }}>Awesomeness starting</Typography>
        </Box>
      )}

      {/* Results Section */}
      {mappedAiSummary && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Analysis Results
          </Typography>
          
          <QualificationDetails
            data={mappedAiSummary}
            editable={true}
            values={editedValues}
            onFieldChange={onFieldChange}
            editingField={editingField}
            onEditField={onEditField}
            onCancelEdit={onCancelEdit}
            onSaveField={onSaveField}
          />
          
          <Divider sx={{ my: 3 }} />
          
          {/* Export Buttons */}
          <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
            <Button variant="outlined" onClick={onExportPDF}>
              Export as PDF
            </Button>
            <Button variant="outlined" onClick={onExportWord}>
              Export as Word
            </Button>
            <Button variant="outlined" onClick={onExportExcel}>
              Export as Excel
            </Button>
          </Box>
          
          {/* Proceed to Next Step Button - This marks step_2 as completed */}
          <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
            <Button
              variant="contained"
              color="success"
              onClick={onProceedToNextStep}
              size="large"
              sx={{ ml: 'auto' }}
            >
              Proceed to ELO Mapping Canvas
            </Button>
          </Box>
          
          {/* AI Prompt Section - HIDDEN */}
          {/* 
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Refine Analysis
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Send prompt to AI (describe your change request)
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={aiPrompt}
                onChange={onAiPromptChange}
                placeholder="Describe what changes you'd like to make to the analysis..."
                variant="outlined"
                size="small"
              />
              <Button
                variant="contained"
                onClick={onSendAIPrompt}
                disabled={aiPromptLoading || !aiPrompt.trim()}
                sx={{ minWidth: 120 }}
              >
                {aiPromptLoading ? <CircularProgress size={20} /> : 'Send'}
              </Button>
            </Box>
          </Paper>
          */}
        </Paper>
      )}
    </Box>
  );
};

export default Step1Interface; 